import React from 'react'
import { Controller, useWatch } from 'react-hook-form'
import Select from 'react-select'
import { CurrencyFormat, DateFormat, PercentFormat } from '../../../../helpers/formatting'
import { reactSelectClassNames } from '../../../../helpers/react-select-styles'
import ManagerApprovalIcons from '../../../../components/elements/ManagerApprovalIcons'

/**
 * Step 2 of the payment-plan wizard — installments editor. Per-row Date /
 * Amount / Method controls (each wrapped in `Controller` from
 * react-hook-form so RHF owns the form state). Last row hosts the action
 * buttons (+ Add Payments, Cancel, Back, Next).
 *
 * Behaviour-preserving extraction from PaymentPlanPayment.jsx. Parent passes
 * `control` from RHF directly so per-cell Controllers stay wired without a
 * mirror layer.
 */
export default function PaymentPlanStep2({
  control,
  collectionResponseData,
  planSelection,
  strategy,
  installmentOptions,
  refreshStepTwo,
  stepTwoNumberOfInstallments,
  setStepTwoNumberOfInstallments,
  paymentPlanPaymentsStep2,
  discountData,
  saving,
  totalAmount,
  setStep,
  checkChangesMade,
  handleSubmit,
  onSubmit,
  watchCustomerPromotionCodeId,
}) {
  // Watch every row's date field so the notice tracks live edits — rows aren't enforced to stay
  // chronological, so any single row (not just the last) can end up past the max date.
  const watchedPaymentDates = useWatch({ control, name: 'payment_date' }) || []
  const latestPaymentDate = Array.from({ length: stepTwoNumberOfInstallments }, (_, index) =>
    watchedPaymentDates[index] || paymentPlanPaymentsStep2[index]?.payment_date)
    .filter(Boolean)
    .reduce((latest, date) => (!latest || date > latest ? date : latest), null)
  const isOverMaxDate =
    !watchCustomerPromotionCodeId?.value
    && !!strategy?.requiresManagerApproval
    && !!latestPaymentDate
    && !!collectionResponseData?.max_date_without_manager_approval
    && latestPaymentDate > collectionResponseData.max_date_without_manager_approval

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="payment_plan_payment_step2_form">
      <p className="title is-5" data-testid="step2-heading">
        Payment Plan - {strategy?.stepTitle} - Step 2 {isOverMaxDate && (
          <span data-testid="step2-duration-approval-notice">
            <ManagerApprovalIcons type="mgr_approval_status_required" />
            <span className="is-size-6 has-text-danger"> Plan completes after <DateFormat dateString={collectionResponseData.max_date_without_manager_approval} /></span>
          </span>
        )}
      </p>
      <table className="table is-narrow table-payment-schedule-panel">
        <tbody>
          <tr data-testid="step2-balance-row-outstanding">
            <td><b>Outstanding Balance</b></td>
            <td><CurrencyFormat value={collectionResponseData?.outstanding_total} /></td>
            <td data-testid="step2-discount-header"><b>{strategy?.includesDiscount ? "Discount" : ""}</b></td>
            <td>{strategy?.includesDiscount ? <PercentFormat value={collectionResponseData?.discount_percent} /> : ""}</td>
            <td></td>
            <td></td>
          </tr>

          {strategy?.includesDiscount && (
            <tr data-testid="step2-balance-row-settlement">
              <td><b>Settlement Balance</b></td>
              <td><CurrencyFormat value={collectionResponseData?.owed_amount} /></td>
              <td><b>Discount</b></td>
              <td><CurrencyFormat value={collectionResponseData?.discount_amount} /></td>
              <td></td>
              <td></td>
            </tr>
          )}

          <tr>
            <td><b>Payment Details</b></td>
            <td colSpan={2}>NOTE: These payments do NOT earn reward points</td>
            <td># of Installments</td>
            <td style={{ minWidth: '100px' }}>
              <Controller
                name="number_of_installments_step2"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    classNames={reactSelectClassNames()}
                    options={installmentOptions}
                    menuPosition="fixed"
                    isSearchable={false}
                    form="payment_plan_payment_step2_form"
                    onChange={(e) => {
                      field.onChange(e); // call the original onChange from react-hook-form
                      refreshStepTwo(e.value);
                    }}
                  />
                )}
              />
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <table className="table is-narrow table-payment-schedule-panel">
        <thead>
          <tr>
            <th>Date</th>
            <th>Amount</th>
            <th>Method</th>
            <th></th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {Array.from({ length: stepTwoNumberOfInstallments }, (_, index) => {
            const plan = paymentPlanPaymentsStep2[index] || {};
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index}>
                <td>
                  <Controller
                    name={`payment_date[${index}]`}
                    rules={{ required: true }}
                    control={control}
                    defaultValue={plan.payment_date || ""}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input is-small"
                        type="date"
                        value={field.value || ""}
                      />
                    )}
                  />
                </td>
                <td>
                  <Controller
                    name={`payment_amount[${index}]`}
                    rules={{ required: true }}
                    control={control}
                    defaultValue={plan.total_amount ? plan.total_amount.toFixed(2) : 0}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input is-small"
                        type="number"
                        min={0}
                        step="0.01"
                        value={field.value || ""}
                      />
                    )}
                  />
                </td>
                <td style={{ minWidth: '150px' }}>
                  <Controller
                    name={`payment_method_dropdown_option_id_step2[${index}]`}
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        classNames={reactSelectClassNames()}
                        options={discountData?.payment_method_dropdown_options.map((o) => ({ label: o.name, value: o.id }))}
                        menuPosition="fixed"
                        isSearchable={false}
                      />
                    )}
                  />
                </td>
                <td>
                  {index == 0 ? <span data-testid="step2-ach-dates-warning">Dates must be valid for ACH to run</span> : ""}
                  {index == stepTwoNumberOfInstallments - 1 && (
                    <div className="is-flex is-align-items-end">
                      <button
                        type="button"
                        data-testid="step2-add-payment"
                        className="button has-background-grey has-text-white is-small mr-1"
                        onClick={() => setStepTwoNumberOfInstallments((prev) => prev + 1)}
                      >
                        <span className="mx-3">+ Add Payments</span>
                      </button>
                    </div>
                  )}
                </td>
                <td>
                  {index == stepTwoNumberOfInstallments - 1 && (
                    <div className="has-text-right">
                      <button
                        type="button"
                        data-testid="step2-cancel"
                        className="button has-background-grey has-text-white is-small mr-1"
                        onClick={() => checkChangesMade()}
                      >
                        <span className="mx-3">Cancel</span>
                      </button>
                      <button
                        type="button"
                        data-testid="step2-back"
                        className="button has-background-grey has-text-white is-small mr-1"
                        onClick={() => setStep("step1")}
                      >
                        <span className="mx-3">Back</span>
                      </button>
                      <button
                        type="submit"
                        data-testid="step2-next"
                        className="button is-link is-small mr-1"
                        form="payment_plan_payment_step2_form"
                        disabled={(saving && 'disabled') || ((parseFloat(collectionResponseData?.owed_amount) != parseFloat(totalAmount.toFixed(2))) && 'disabled')}
                      >
                        <span className="mx-3">{saving ? 'Submitting' : 'Next'}</span>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
          <tr data-testid="step2-balance-row-remaining">
            <td></td>
            <td><b>Remaining:</b> <CurrencyFormat value={parseFloat(collectionResponseData?.owed_amount) - parseFloat(totalAmount)} /></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </form>
  )
}
